import axios, { AxiosError, AxiosResponse } from "axios";
import { store } from "../../store/store";
import {
  hideSnackbar,
  showSnackbar,
} from "../../store/actions/snackbarActions";
import { IApiResponse } from "../../interfaces/common/IApiResponse";
import { NavigateFunction } from "react-router-dom";
import { COMMON } from "../../constants/common";
import { actionTypes } from "../../store/actionTypes/actionTypes";
import { RouterPathEnum } from "../../constants/router";

const handleSuccess = <T>(response: AxiosResponse<T>): IApiResponse<T> => {
  return { success: true, data: response.data };
};

/**
 * Extracts a human-readable error message from various error formats
 */
const extractErrorMessage = (errorData: any): string => {
  if (!errorData) return "Unknown error";

  // If it's already a string, return it
  if (typeof errorData === "string") return errorData;

  // Handle FastAPI/Pydantic validation error format: { detail: [{ loc, msg, type }] }
  if (Array.isArray(errorData)) {
    const messages = errorData
      .map((err: any) => {
        if (typeof err === "string") return err;
        if (err.msg) {
          // Extract field name from loc array (e.g., ["body", "email"] -> "email")
          const field = err.loc ? err.loc[err.loc.length - 1] : "";
          return field ? `${field}: ${err.msg}` : err.msg;
        }
        return String(err);
      })
      .join(", ");
    return messages || "Validation error";
  }

  // Handle object with detail that is an array (FastAPI validation errors)
  if (errorData.detail && Array.isArray(errorData.detail)) {
    return extractErrorMessage(errorData.detail);
  }

  // Handle object with detail string
  if (errorData.detail && typeof errorData.detail === "string") {
    return errorData.detail;
  }

  // Handle object with message string
  if (errorData.message && typeof errorData.message === "string") {
    return errorData.message;
  }

  // Handle object with error string
  if (errorData.error && typeof errorData.error === "string") {
    return errorData.error;
  }

  // Fallback: try to stringify, but avoid "[object Object]"
  try {
    const stringified = JSON.stringify(errorData);
    if (stringified && stringified !== "{}") {
      return stringified.length > 200 ? stringified.substring(0, 200) + "..." : stringified;
    }
  } catch {
    // Ignore stringify errors
  }

  return "An unexpected error occurred";
};

const handleError = (error: AxiosError): string => {
  if (error.response) {
    return extractErrorMessage(error.response.data) || "Unknown server error";
  } else if (error.request) {
    return "No response from server";
  }
  return "";
};

const handleApiError = (
  error: AxiosError,
  navigate?: NavigateFunction,
  errorCallback?: (error: any) => void
): void => {
  const status = error.response?.status;
  const responseData = error.response?.data as any;

  // Extract error detail - handle various formats
  let errorDetail: string;
  if (responseData?.detail) {
    errorDetail = extractErrorMessage(responseData.detail);
  } else {
    errorDetail = handleError(error);
  }

  // Check for specific user status errors first (before generic 403/404 handling)
  if (errorDetail === "UserDoesNotExists") {
    // Redirect to user request page if user doesn't exist
    if (navigate) {
      navigate(`/${RouterPathEnum.USER_REQUEST}`);
    }
    // Also call errorCallback if provided (for custom handling)
    if (errorCallback) {
      errorCallback(error);
    }
    return;
  }

  if (errorDetail === "UserInactive") {
    // Redirect to user inactive page if user is inactive
    if (navigate) {
      navigate(`/${RouterPathEnum.USER_INACTIVE}`);
    }
    // Also call errorCallback if provided (for custom handling)
    if (errorCallback) {
      errorCallback(error);
    }
    return;
  }

  if (status === 401) {
    if (navigate) {
      navigate(`/${RouterPathEnum.LOGIN}`);
    }
    store.dispatch(showSnackbar(COMMON.TOKEN_EXPIRED_MESSAGE, "info"));
  } else if (status === 403) {
    if (navigate) {
      navigate(`/${RouterPathEnum.ACCESS_DENIED}`);
    }
  } else if (status === 409) {
    // Conflict error - usually duplicate email
    store.dispatch(showSnackbar(errorDetail, "error"));
    store.dispatch({
      type: actionTypes.UPDATE_LOADER_STATE,
      payload: false,
    });
  } else if (status === 502) {
    const apiError = COMMON.ERROR_MESSAGE.GATEWAY_ERROR_MESSAGE;
    store.dispatch(showSnackbar(apiError, "error"));
  } else {
    const apiError = errorDetail;
    if (apiError) {
      store.dispatch(showSnackbar(apiError, "error"));
      store.dispatch({
        type: actionTypes.UPDATE_LOADER_STATE,
        payload: false,
      });
    }
  }

  if (errorCallback) {
    errorCallback(error);
  }

  setTimeout(() => {
    store.dispatch(hideSnackbar());
  }, 5000);
};

const getAuthHeaders = (isAuthRequired?: any): Record<string, string> | {} => {
  if (isAuthRequired?.isAuthRequired) {
    return {
      Authorization: `Bearer ${localStorage.getItem(COMMON.ACCESS_TOKEN)}`,
    };
  }
  return {};
};

export const postDataFromApi = async <T>(
  url: string,
  payload: any | Array<any>,
  callback: (response: IApiResponse<T>) => void,
  isAuthRequired?: any,
  navigate?: NavigateFunction,
  errorCallback?: (error: any) => void
): Promise<void> => {
  try {
    const response = await axios.post<T>(url, payload, {
      headers: getAuthHeaders(isAuthRequired),
    });

    const apiResponse = handleSuccess(response);
    callback(apiResponse);
  } catch (axiosError) {
    handleApiError(axiosError as AxiosError, navigate, errorCallback);
  }
};

export const putDataFromApi = async <T>(
  url: string,
  payload: any | Array<any>,
  callback: (response: IApiResponse<T>) => void,
  isAuthRequired?: any,
  navigate?: NavigateFunction,
  errorCallback?: (error: any) => void
): Promise<void> => {
  try {
    const response = await axios.put<T>(url, payload, {
      headers: getAuthHeaders(isAuthRequired),
    });

    const apiResponse = handleSuccess(response);
    callback(apiResponse);
  } catch (error) {
    handleApiError(error as AxiosError, navigate, errorCallback);
  }
};

export const getDataFromApi = async <T>(
  url: string,
  callback: (response: IApiResponse<T>) => void,
  isAuthRequired?: any,
  navigate?: NavigateFunction,
  errorCallback?: (error: any) => void
): Promise<void> => {
  try {
    const response = await axios.get<T>(url, {
      headers: getAuthHeaders(isAuthRequired),
    });

    const apiResponse = handleSuccess(response);
    callback(apiResponse);
  } catch (error) {
    handleApiError(error as AxiosError, navigate, errorCallback);
  }
};

export const deleteDataFromApi = async <T>(
  url: string,
  payload: any | Array<any>,
  callback: (response: IApiResponse<T>) => void,
  isAuthRequired?: any,
  navigate?: NavigateFunction,
  errorCallback?: (error: any) => void
): Promise<void> => {
  try {
    const response = await axios.delete<T>(url, {
      data: payload,
      headers: getAuthHeaders(isAuthRequired),
    });

    const apiResponse = handleSuccess(response);
    callback(apiResponse);
  } catch (error) {
    handleApiError(error as AxiosError, navigate, errorCallback);
  }
};

export const patchDataFromApi = async <T>(
  url: string,
  payload: any | Array<any>,
  callback: (response: IApiResponse<T>) => void,
  isAuthRequired?: any,
  navigate?: NavigateFunction,
  errorCallback?: (error: any) => void
): Promise<void> => {
  try {
    const response = await axios.patch<T>(url, payload, {
      headers: getAuthHeaders(isAuthRequired),
    });

    const apiResponse = handleSuccess(response);
    callback(apiResponse);
  } catch (error) {
    handleApiError(error as AxiosError, navigate, errorCallback);
  }
};